import { useEffect, useRef } from "react";
import type { Editor } from "./canvas";

/** One field edit or slider gesture is one action, including inspector handlers
 * that already request their own stopping point. Text canvases own their typing
 * history separately; this boundary only handles native form controls. */
export function useHistoryControls(editor: Editor) {
  const active = useRef<{ element: HTMLElement; mark: string } | null>(null);
  const control = (target: EventTarget) =>
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
      ? target
      : null;
  const begin = (target: EventTarget) => {
    const element = control(target);
    if (
      !element ||
      (element instanceof HTMLInputElement &&
        ["file", "checkbox", "radio", "button", "submit"].includes(
          element.type,
        ))
    )
      return;
    if (active.current?.element === element) return;
    if (active.current) editor.commitHistoryStoppingPoint(active.current.mark);
    const label =
      element.getAttribute("aria-label") ||
      element.labels?.[0]?.textContent?.trim().split("\n")[0] ||
      "property";
    active.current = {
      element,
      mark: editor.beginHistoryInput(`Change ${label.slice(0, 80)}`),
    };
  };
  const end = (target: EventTarget) => {
    if (active.current?.element !== target) return;
    editor.commitHistoryStoppingPoint(active.current.mark);
    active.current = null;
  };
  useEffect(() => {
    const focus = (e: Event) => {
      if (e.target && !editor.getIsReadonly()) begin(e.target);
    };
    const blur = (e: Event) => {
      if (e.target) end(e.target);
    };
    const range = (fn: (target: EventTarget) => void) => (e: Event) => {
      if (e.target instanceof HTMLInputElement && e.target.type === "range")
        fn(e.target);
    };
    const listeners: [string, (e: Event) => void][] = [
      ["focusin", focus],
      ["focusout", blur],
      ["pointerdown", range(begin)],
      ["pointerup", range(end)],
      ["pointercancel", blur],
      ["keydown", range(begin)],
      ["keyup", range(end)],
    ];
    for (const [name, fn] of listeners)
      document.addEventListener(name, fn, true);
    return () => {
      for (const [name, fn] of listeners)
        document.removeEventListener(name, fn, true);
      if (active.current)
        editor.commitHistoryStoppingPoint(active.current.mark);
    };
  }, [editor]);
}
